/**
 * Detailer v4: mega menu content width.
 *
 * The mega menu panel is `position: absolute; left: 0; width: 100%` inside
 * `.header__row` (the first positioned ancestor), so its content box is the
 * viewport minus one `--page-margin` on each side. The top-level nav list is a
 * separate subtree, `ul[part="list"]` inside `<overflow-list>`'s shadow root -
 * whose width is intrinsic (max-content of the links) and therefore not
 * expressible as any CSS length available to the panel.
 *
 * This module measures the true first-item-to-last-item extent of the nav and
 * publishes it as two custom properties on `#header-component`:
 *
 *   --detailer-nav-inline-start  offset from the header row's inline-start edge
 *   --detailer-nav-inline-size   width of the content box
 *
 * sections/header.liquid consumes both with fallbacks that reproduce today's
 * layout exactly, so if this file fails to load or throws, the panel renders as
 * it did before rather than collapsing.
 *
 * Why the items and not the list box: `.overflow-menu:part(list)` carries
 * `margin-inline: calc(-1 * var(--menu-horizontal-gap) / 2)`, `.overflow-menu`
 * carries `padding: var(--focus-outline-size); margin: calc(-1 * ...)`, and
 * assets/overflow-list.js parks a sized `[part="placeholder"]` flex item at the
 * end of the list while items are collapsed into "More". All three make the host
 * box wider than the extent a person actually sees.
 *
 * Contract: reads first, writes second, one rAF per burst, custom-property
 * writes only. Inert below 750px and whenever the menu is not laid out.
 */

const HOST_ID = 'header-component';
const NAV_SELECTOR = 'nav.menu-list';
const ITEM_SELECTOR = ':scope > li.menu-list__list-item';
const PROP_START = '--detailer-nav-inline-start';
const PROP_SIZE = '--detailer-nav-inline-size';

/**
 * Desktop gate. Mirrors the theme's own breakpoint for showing `.header-menu`
 * (sections/header.liquid: `@media (min-width: 750px)` +
 * `#header-component[data-menu-style='menu']`). Below it the drawer is used and
 * there is no mega menu to size, so the module publishes nothing.
 */
const DESKTOP_QUERY = '(min-width: 750px)';

/**
 * Guardrail. When the nav is very narrow: few top-level items, a short
 * localisation, matching it exactly would crush the link columns and the promo
 * card. The content box is clamped up to this width, stays centred on the nav,
 * and is then capped by the header row's own content box so it can never grow
 * past today's layout.
 */
const MIN_CONTENT_WIDTH = 640;

/** Sub-pixel churn is not worth a style write. */
const EPSILON = 0.5;

/** @type {{ host: HTMLElement, teardown: () => void } | null} */
let binding = null;
let frame = 0;
/** @type {number | null} */
let publishedStart = null;
/** @type {number | null} */
let publishedSize = null;

const desktop = window.matchMedia(DESKTOP_QUERY);

/** @param {number} value */
const round = (value) => Math.round(value * 100) / 100;

function schedule() {
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    try {
      update();
    } catch {
      // Never let a measurement error break the header. The CSS fallbacks hold.
    }
  });
}

/** @param {HTMLElement} host */
function clear(host) {
  if (publishedStart === null && publishedSize === null) return;
  host.style.removeProperty(PROP_START);
  host.style.removeProperty(PROP_SIZE);
  publishedStart = null;
  publishedSize = null;
}

/**
 * The visible top-level items of one nav, in document order.
 * Excludes items parked in the "More" panel (`slot="overflow"`, rendered inside
 * the dropdown, not the bar), items hidden by header-menu.js
 * (`aria-hidden="true"`), and anything with no box, which is how the "More"
 * item itself drops out, since assets/overflow-list.js hides its shadow slot
 * until something actually overflows.
 *
 * @param {Element} nav
 * @returns {DOMRect[]}
 */
function visibleItemRects(nav) {
  const list = nav.querySelector('overflow-list') ?? nav;
  /** @type {DOMRect[]} */
  const rects = [];

  for (const item of list.querySelectorAll(ITEM_SELECTOR)) {
    if (item.getAttribute('slot') === 'overflow') continue;
    if (item.getAttribute('aria-hidden') === 'true') continue;

    const rect = item.getBoundingClientRect();
    if (rect.width > 0) rects.push(rect);
  }

  return rects;
}

/**
 * The header row's own content box, in viewport coordinates. This is the
 * reference the previous fix used; here it is only the outer bound, so the
 * clamped-up minimum can never push the panel past the header's own gutters.
 *
 * @param {Element} row
 * @returns {{ left: number, right: number }}
 */
function rowContentBox(row) {
  const columns = row.querySelector(':scope > .header__columns');
  const rect = (columns ?? row).getBoundingClientRect();

  if (!columns) return { left: rect.left, right: rect.right };

  const style = getComputedStyle(columns);
  return {
    left: rect.left + (parseFloat(style.paddingLeft) || 0),
    right: rect.right - (parseFloat(style.paddingRight) || 0),
  };
}

function update() {
  if (!binding) return;

  const { host } = binding;

  if (!host.isConnected) {
    attach();
    return;
  }

  if (!desktop.matches) {
    clear(host);
    return;
  }

  // ---- read phase -------------------------------------------------------
  /** @type {Element | null} */
  let nav = null;
  /** @type {DOMRect[]} */
  let rects = [];

  for (const candidate of host.querySelectorAll(NAV_SELECTOR)) {
    const candidateRects = visibleItemRects(candidate);
    if (candidateRects.length > 0) {
      nav = candidate;
      rects = candidateRects;
      break;
    }
  }

  // Drawer mode, an empty menu, or a header that is not laid out yet.
  if (!nav || rects.length === 0) {
    clear(host);
    return;
  }

  // `.header__row` is the mega menu panel's containing block
  // (`position: relative` in sections/header.liquid) and has no inline padding
  // or border, so its border-box origin is where the panel's `left: 0` starts
  // and where the panel's grid area begins.
  const row = nav.closest('.header__row') ?? host;
  const rowRect = row.getBoundingClientRect();
  if (rowRect.width <= 0) {
    clear(host);
    return;
  }

  let navLeft = Infinity;
  let navRight = -Infinity;
  for (const rect of rects) {
    if (rect.left < navLeft) navLeft = rect.left;
    if (rect.right > navRight) navRight = rect.right;
  }
  if (!(navRight > navLeft)) {
    clear(host);
    return;
  }

  const bounds = rowContentBox(row);
  const available = bounds.right - bounds.left;
  if (available <= 0) {
    clear(host);
    return;
  }

  const isRtl = getComputedStyle(row).direction === 'rtl';

  // ---- derive -----------------------------------------------------------
  const navWidth = navRight - navLeft;
  const minimum = Math.min(MIN_CONTENT_WIDTH, available);
  const size = Math.min(Math.max(navWidth, minimum), available);

  // Centre the (possibly clamped-up) box on the nav's own centre, then keep it
  // inside the header row's content box. When the nav is wider than the
  // minimum, the normal case, this is an exact match: the box starts at the
  // first item's leading edge and ends at the last item's trailing edge.
  let left = (navLeft + navRight) / 2 - size / 2;
  left = Math.min(Math.max(left, bounds.left), bounds.right - size);

  // Published as a logical offset so `margin-inline-start` and the "More"
  // panel's first grid track are correct in RTL as well as LTR.
  const inlineStart = isRtl ? rowRect.right - (left + size) : left - rowRect.left;

  // ---- write phase ------------------------------------------------------
  if (publishedSize === null || Math.abs(publishedSize - size) > EPSILON) {
    host.style.setProperty(PROP_SIZE, `${round(size)}px`);
    publishedSize = size;
  }

  if (publishedStart === null || Math.abs(publishedStart - inlineStart) > EPSILON) {
    host.style.setProperty(PROP_START, `${round(inlineStart)}px`);
    publishedStart = inlineStart;
  }
}

function detach() {
  binding?.teardown();
  binding = null;
  publishedStart = null;
  publishedSize = null;
}

function attach() {
  detach();

  const host = document.getElementById(HOST_ID);
  if (!host) return;

  /** @type {Array<() => void>} */
  const cleanups = [];

  const resizeObserver = new ResizeObserver(schedule);
  cleanups.push(() => resizeObserver.disconnect());
  resizeObserver.observe(host);

  const attributeObserver = new MutationObserver(schedule);
  cleanups.push(() => attributeObserver.disconnect());

  for (const nav of host.querySelectorAll(NAV_SELECTOR)) {
    resizeObserver.observe(nav);

    // assets/overflow-list.js moves items in and out of the "More" slot as the
    // viewport narrows by writing `slot` on the light-DOM <li>s, and
    // header-menu.js toggles `aria-hidden`. Either changes the nav's extent.
    attributeObserver.observe(nav, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['slot', 'aria-hidden'],
    });

    const overflowList = nav.querySelector('overflow-list');
    const shadow = overflowList?.shadowRoot;
    if (!shadow) continue;

    // The declarative shadow list itself: its box changes on reflow even when
    // no light-DOM attribute does.
    const shadowList = shadow.querySelector('[part="list"]');
    if (shadowList) resizeObserver.observe(shadowList);

    // `moreSlot.hidden` flips when overflow starts or stops, which adds or
    // removes the "More" item from the measured extent.
    const shadowObserver = new MutationObserver(schedule);
    shadowObserver.observe(shadow, {
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });
    cleanups.push(() => shadowObserver.disconnect());
  }

  // Opening a panel is a moment the measurement must already be correct.
  for (const menu of host.querySelectorAll('header-menu')) {
    attributeObserver.observe(menu, { attributes: true, attributeFilter: ['aria-expanded'] });
  }

  const onResize = () => schedule();
  window.addEventListener('resize', onResize, { passive: true });
  cleanups.push(() => window.removeEventListener('resize', onResize));

  desktop.addEventListener('change', onResize);
  cleanups.push(() => desktop.removeEventListener('change', onResize));

  binding = {
    host,
    teardown: () => {
      for (const cleanup of cleanups) cleanup();
    },
  };

  // Web fonts change the intrinsic width of every link.
  document.fonts?.ready.then(schedule).catch(() => {});

  schedule();
}

// The theme editor re-renders the header section; rebind to the new DOM.
document.addEventListener('shopify:section:load', (event) => {
  if (/** @type {HTMLElement} */ (event.target)?.querySelector?.(`#${HOST_ID}`)) attach();
});
document.addEventListener('shopify:section:unload', (event) => {
  if (/** @type {HTMLElement} */ (event.target)?.querySelector?.(`#${HOST_ID}`)) detach();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', attach, { once: true });
} else {
  attach();
}
